import {
  Button,
  FormHelperText,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import {
  formatDollarAmount,
  important,
  validateSchema,
} from '../../../utils/global/helper';
import { getInputBorderStyle } from '../../../helpers/styles';
import {
  CheckoutCustomer,
  getSplitPaymentAmountSchema,
} from '../../../types/payments';
import useSplitPayments from '../../../hooks/payments/useSplitPayments';
import useSplitPaymentsState from '../../../hooks/payments/useSplitPaymentsState';
import {
  calculateFeeAmount,
  getPaymentMethodFee,
} from '../../../helpers/payments';
import { CheckoutPaymentMethod } from '../../../types/appointments';
import useUser from '../../../hooks/global/useUser';
import useGetCustomerGiftCardBalance from '../../../hooks/giftCards/useGetCustomerGiftCardBalance';
import useCashDiscount from '../../../hooks/payments/useCashDiscount';

interface Props {
  value: string;
  onChange: (value: string) => void;
  maxFaceAmount: number;
  effectiveTotalPriceCents: number;
  disabled?: boolean;
  paymentMethod: CheckoutPaymentMethod;
  customer: CheckoutCustomer;
  isGiftCardError?: boolean;
}

function AmountInput({
  value,
  onChange,
  maxFaceAmount,
  effectiveTotalPriceCents,
  disabled,
  paymentMethod,
  customer,
  isGiftCardError,
}: Props) {
  const { user } = useUser();
  const { cashDiscountEnabled } = useCashDiscount();
  const { paymentMethods, currentIndex } = useSplitPayments();
  const { effectiveAmountCents, isCurrentFeelessUnderCd } =
    useSplitPaymentsState();

  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const theme = useTheme();

  const hasIdentifier = !!customer.customerId || !!customer.phoneNumber;
  const { data: giftCardBalance, isFetching: isFetchingGiftCardBalance } =
    useGetCustomerGiftCardBalance({
      customer,
      enabled: hasIdentifier && paymentMethod === 'GIFT_CARD',
    });

  const validationSchema = getSplitPaymentAmountSchema(
    effectiveTotalPriceCents,
    paymentMethods,
    currentIndex,
  );
  const { isValid, errors } = validateSchema(validationSchema, value);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsTouched(true);
    setIsFocused(false);

    // any number and ONLY ONE PERIOD
    const filteredValue = e.target.value.replace(/[^0-9.]/g, '');
    const periodCount = (filteredValue.match(/\./g) || []).length;
    if (periodCount > 1) {
      e.target.value = filteredValue.replace(/\.+/, '.');
    }
    if (e.target.value.endsWith('.')) {
      e.target.value = e.target.value.slice(0, -1);
    }
    onChange(e.target.value);
  };

  const onFocus = () => {
    setIsFocused(true);
  };

  const numberValue = !Number.isNaN(Number(value)) ? Number(value) : 0;
  const feeRate = paymentMethod
    ? getPaymentMethodFee(paymentMethod, user?.business)
    : 0;
  const feeAmount = calculateFeeAmount(numberValue, feeRate, paymentMethod);
  const totalAmount = numberValue + feeAmount;

  const error = !!isGiftCardError || (!isValid && isTouched);

  return (
    <Stack
      gap={0.25}
      sx={{
        '& div.Mui-disabled': {
          bgcolor: theme.palette.lightGrey.main,
        },
        '& input.Mui-disabled, & .MuiSelect-select.Mui-disabled': {
          bgcolor: important('transparent'),
        },
      }}>
      <TextField
        label="Payment amount"
        variant="outlined"
        prefix="$"
        placeholder={formatDollarAmount(maxFaceAmount, true, true)}
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        onFocus={onFocus}
        sx={{
          fontFamily: theme.typography.fontFamily,
          '& .MuiOutlinedInput-notchedOutline': getInputBorderStyle({
            isError: error,
            theme,
          }),
        }}
        slotProps={{
          input: {
            disabled,
            startAdornment:
              isFocused || !!value.length ? (
                <InputAdornment
                  sx={{
                    mr: 0,
                  }}
                  position="start">
                  $
                </InputAdornment>
              ) : null,
            endAdornment:
              !disabled && maxFaceAmount > 0 ? (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    disableElevation
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => onChange(maxFaceAmount.toFixed(2))}
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: theme.typography.caption.fontSize,
                      fontWeight: 600,
                      textTransform: 'none',
                      letterSpacing: 0,
                      color: theme.palette.primary.main,
                      bgcolor: `${theme.palette.primary.main}14`,
                      '&:hover': {
                        bgcolor: `${theme.palette.primary.main}22`,
                        borderColor: `${theme.palette.primary.main}66`,
                      },
                    }}>
                    Max
                  </Button>
                </InputAdornment>
              ) : null,
          },
          htmlInput: {
            sx: {
              fontSize: theme.typography.body2.fontSize,
              color: 'text.secondary',
              px: 2,
              py: 3,
            },
          },
          inputLabel: {
            sx: {
              color: 'text.secondary',
              lineHeight: 1.25,
            },
          },
        }}
      />
      {feeAmount > 0 && isValid && !cashDiscountEnabled && (
        <FormHelperText sx={{ color: 'text.secondary' }}>
          <Typography
            variant="inherit"
            fontWeight={700}
            component="span"
            color="inherit">
            Total of {formatDollarAmount(totalAmount)}
          </Typography>{' '}
          will be charged to the customer&apos;s payment method
        </FormHelperText>
      )}
      {isCurrentFeelessUnderCd && isValid && numberValue > 0 && (
        <FormHelperText sx={{ color: 'text.secondary' }}>
          <Typography
            variant="inherit"
            fontWeight={700}
            component="span"
            color="inherit">
            {formatDollarAmount(effectiveAmountCents / 100)}
          </Typography>{' '}
          will be deducted from total amount
        </FormHelperText>
      )}
      {paymentMethod === 'GIFT_CARD' && (
        <FormHelperText
          sx={{
            color:
              isGiftCardError && !isFetchingGiftCardBalance
                ? theme.palette.error.dark
                : 'text.secondary',
          }}>
          {isFetchingGiftCardBalance ? (
            'Loading gift card balance...'
          ) : (
            <>
              <Typography variant="inherit" component="span" color="inherit">
                Available gift card balance:
              </Typography>{' '}
              <Typography
                variant="inherit"
                fontWeight={700}
                component="span"
                color="inherit">
                {formatDollarAmount(
                  (giftCardBalance?.availableBalance ?? 0) / 100,
                  true,
                )}
              </Typography>
            </>
          )}
        </FormHelperText>
      )}
      {error && errors.length > 0 && (
        <FormHelperText sx={{ color: theme.palette.error.dark }}>
          {errors[0]}
        </FormHelperText>
      )}
    </Stack>
  );
}

export default AmountInput;
